/**
 * @author       grady@zumper.com (Grady Kuhnline <gradykuhnline>)
 * @copyright    Copyright (c) 2019, Zumper
 * @description
 */

import { Renderable } from '@zcode/render-props'
import hoistStatics from 'hoist-non-react-statics'
import React, { useCallback } from 'react'
import { ErrorBoundaryProps, FallbackProps } from 'react-error-boundary'

import {
  createLoadableSection,
  CreateLoadableSectionOptions,
  LoadFn,
} from './createLoadableSection'
import { LazyLoadableSectionProps } from './LazyLoadableSection'
import { MemoLazyLoadableSection } from './MemoLazyLoadableSection'
import { ErgonomicUnion } from './types'

export interface CreateLazyLoadableSectionOptions<Props extends object>
  extends Omit<
    CreateLoadableSectionOptions<Props>,
    'errorFallback' | 'loadingFallback'
  > {
  // This errorFallback gets passed to createLoadableSection, which will pass it Props and
  // to MemoLazyLoadableSection, which will pass it error and resetErrorBoundary
  errorFallback?: React.ComponentType<ErgonomicUnion<FallbackProps, Props>>
  loadingFallback?: Renderable<Props>
  onLoadingError?: (error: unknown) => void
  onRenderError?: ErrorBoundaryProps['onError']
}

export interface OuterLazyLoadableSectionProps<ForwardProps extends object>
  extends Pick<
    LazyLoadableSectionProps<ForwardProps>,
    | 'as'
    | 'fallbackStyle'
    | 'forceInView'
    | 'lazySectionClassName'
    | 'onRenderError'
    | 'root'
    | 'rootMargin'
    | 'threshold'
  > {
  onLoadingError?: (error: unknown) => void
}

export const createLazyLoadableSection = <Props extends object>(
  loadFn: LoadFn<Props> | { requireAsync: LoadFn<Props> },
  {
    errorFallback,
    loadingFallback,
    onLoadingError,
    onRenderError,
  }: CreateLazyLoadableSectionOptions<Props>
) => {
  // allow for onLoadingError as a prop
  let innerOnLoadingError: undefined | ((error: unknown) => void)
  const boundOnLoadingError = (error: unknown) => {
    if (innerOnLoadingError) {
      innerOnLoadingError(error)
    }
    if (onLoadingError) {
      onLoadingError(error)
    }
  }

  // create a LoadableSection
  const LoadableSection = createLoadableSection<Props>(loadFn, {
    loadingFallback,
    // @ts-expect-error
    errorFallback,
    onLoadingError: boundOnLoadingError,
  })

  // create an HOC
  const OuterLazyLoadableSection = ({
    as,
    fallbackStyle,
    forceInView,
    lazySectionClassName,
    onLoadingError: propOnLoadingError,
    onRenderError: propOnRenderError,
    root,
    rootMargin = '300px',
    threshold,
    ...forwardProps
  }: Props & OuterLazyLoadableSectionProps<Props>) => {
    innerOnLoadingError = propOnLoadingError
    const handleRenderError = useCallback(
      (...args: [error: Error, info: { componentStack: string }]) => {
        try {
          if (onRenderError) {
            onRenderError(...args)
          }
          if (propOnRenderError) {
            propOnRenderError(...args)
          }
        } catch (e) {}
      },
      [propOnRenderError]
    )
    return (
      <MemoLazyLoadableSection<Props>
        as={as}
        // @ts-expect-error
        errorFallback={errorFallback}
        fallbackStyle={fallbackStyle}
        forceInView={forceInView}
        forwardProps={forwardProps as Props}
        lazySectionClassName={lazySectionClassName}
        loadable={LoadableSection}
        loadingFallback={loadingFallback}
        onRenderError={
          onRenderError ?? propOnRenderError ? handleRenderError : undefined
        }
        root={root}
        rootMargin={rootMargin}
        threshold={threshold}
      />
    )
  }
  return hoistStatics(OuterLazyLoadableSection, LoadableSection)
}
